import { useEffect, useState } from "react";
import { Box, Fade, Slide, useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useSessionStorage } from "src/utils/useSessionStorage.js";
import { useDimensions } from "src/utils/useDimensions.js";
import {
  CONTENT_GUTTER,
  SIDEBAR_TRANSITION,
  SIDEBAR_TRANSITION_MS,
  TOPBAR_HEIGHT,
} from "src/styles/layout.js";
import DiscoveryTopbar from "./DiscoveryTopbar.js";
import DiscoverySidebar, {
  COMPACT_SIDEBAR_WIDTH,
  SIDEBAR_WIDTH,
} from "./DiscoverySidebar.js";
import BottomTabBar, { BOTTOM_TAB_BAR_HEIGHT } from "./BottomTabBar.js";
import BrowseSheet from "./BrowseSheet.js";
import ExpandedFooter from "src/components/blocks/footers/ExpandedFooter.js";
import Footer from "src/components/blocks/footers/Footer.js";
import { isCatalogPath, resolveCollapsed } from "./sidebarVisibility.js";

/** Whether the previous route was a catalog page. Lives outside the component
 *  because each route wraps its own shell, so this remounts on navigation. */
const WAS_CATALOG_KEY = "discoveryWasCatalog";

/** Width of the optional right-hand rail column. */
const RIGHT_RAIL_WIDTH = 320;

/**
 * App-wide layout: a sticky topbar, an optional desktop rail, and a mobile
 * drawer. Catalog pages expand their sidebar in-flow; content pages expand it
 * as an overlay so centered content never shifts horizontally.
 *
 * @param {{ children: React.ReactNode, rightRail?: React.ReactNode,
 *   sidebar?: boolean, variant?: "light"|"dark", disableGutters?: boolean,
 *   bottomTabBar?: boolean,
 *   footer?: "expanded"|"expanded-dark"|"minimal"|"none" }} props
 *   `sidebar` opts a page out of the nav column entirely; `disableGutters` lets
 *   full-bleed pages own their own padding; `bottomTabBar` hides the persistent
 *   mobile tab bar for pages with their own bottom UI (e.g. search). `footer`
 *   picks the site footer: the light multi-column one for discovery/consumer
 *   pages, its dark twin for the creator pages, a minimal bar for in-app utility
 *   pages, or none.
 */
const DiscoveryShell = ({
  children,
  rightRail,
  sidebar = true,
  variant = "light",
  disableGutters = false,
  bottomTabBar = true,
  footer = "expanded",
}) => {
  const { isMediumScreen } = useDimensions();
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const transitionMs = prefersReducedMotion ? 0 : SIDEBAR_TRANSITION_MS;
  const { pathname } = useLocation();
  const [browseOpen, setBrowseOpen] = useState(false);
  // Default the collapsed state from the landing path so content pages (where
  // the nav is a fixed overlay) never flash the overlay open on the first
  // render before the effect below settles it. Without this, `/library` and
  // other content routes briefly mount the fixed overlay sidebar over the
  // page, whose active NavLink intercepts/detaches the first click on the
  // content beneath it.
  const [collapsed, setCollapsed] = useSessionStorage(
    "discoverySidebarCollapsed",
    !isCatalogPath(pathname)
  );

  // Collapse when leaving the catalog, open when entering it from outside.
  useEffect(() => {
    const isCatalog = isCatalogPath(pathname);
    let wasCatalog = null;
    try {
      const stored = window.sessionStorage.getItem(WAS_CATALOG_KEY);
      if (stored !== null) wasCatalog = stored === "true";
      window.sessionStorage.setItem(WAS_CATALOG_KEY, String(isCatalog));
    } catch {
      // private mode — fall back to treating this as a fresh session
    }
    setCollapsed((stored) => resolveCollapsed({ isCatalog, wasCatalog, stored }));
  }, [pathname, setCollapsed]);

  const isCatalog = isCatalogPath(pathname);
  const sidebarExpanded = sidebar && !collapsed;
  const sidebarOverlaysContent = sidebarExpanded && !isCatalog;
  const desktopSidebarWidth = sidebar
    ? isCatalog && sidebarExpanded
      ? SIDEBAR_WIDTH
      : COMPACT_SIDEBAR_WIDTH
    : null;
  const stickyContentTop = TOPBAR_HEIGHT;

  // Close the browse sheet on navigation. Each route re-renders this shell at
  // the same tree position, so the instance (and its state) persists across
  // navigations — without this the sheet stays open after a tap.
  useEffect(() => {
    setBrowseOpen(false);
  }, [pathname]);

  // Insets of the content column (and of the fixed mobile tab bar), for
  // overlays that centre on it or must clear the bar. Published on the
  // document element rather than this subtree because `#portal-root` is a
  // sibling of the React root and inherits nothing from here.
  const hasRightRail = Boolean(rightRail);
  useEffect(() => {
    const { style } = document.documentElement;
    const left = isMediumScreen ? 0 : (desktopSidebarWidth ?? 0);
    const right = isMediumScreen || !hasRightRail ? 0 : RIGHT_RAIL_WIDTH;
    const bottom =
      isMediumScreen && sidebar && bottomTabBar
        ? `calc(${BOTTOM_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`
        : "0px";
    style.setProperty("--discovery-content-left", `${left}px`);
    style.setProperty("--discovery-content-right", `${right}px`);
    style.setProperty("--discovery-content-bottom", bottom);
    return () => {
      style.removeProperty("--discovery-content-left");
      style.removeProperty("--discovery-content-right");
      style.removeProperty("--discovery-content-bottom");
    };
  }, [isMediumScreen, desktopSidebarWidth, hasRightRail, sidebar, bottomTabBar]);

  const handleToggle = () => {
    if (isMediumScreen) setBrowseOpen((open) => !open);
    else setCollapsed((value) => !value);
  };

  const columns = [
    desktopSidebarWidth ? `${desktopSidebarWidth}px` : null,
    "1fr",
    rightRail ? `${RIGHT_RAIL_WIDTH}px` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: variant === "dark" ? "black" : "paper.primary",
        "--discovery-header-height": `${stickyContentTop}px`,
      }}
    >
      <DiscoveryTopbar
        onMenuClick={handleToggle}
        variant={variant}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: columns },
          // Ease the sidebar column open/closed instead of snapping when the
          // collapsed state flips on navigation.
          transition: prefersReducedMotion
            ? "none"
            : `grid-template-columns ${SIDEBAR_TRANSITION}`,
          alignItems: "start",
          position: "relative",
        }}
      >
        {sidebar && (
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "sticky",
              top: `${stickyContentTop}px`,
              maxHeight: `calc(100vh - ${stickyContentTop}px)`,
              minWidth: 0,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {/* Crossfade between the compact rail and the full nav so the
                content settles as the column ease resizes it, instead of
                popping in at the new width. Keyed on the mode so MUI runs the
                enter transition on each swap. */}
            {!sidebarOverlaysContent && (
              <Fade
                key={!sidebarExpanded || !isCatalog ? "compact" : "full"}
                in
                appear
                timeout={transitionMs}
              >
                <Box>
                  <DiscoverySidebar
                    compact={!sidebarExpanded || !isCatalog}
                    variant={variant}
                    onBrowseClick={() => setCollapsed(false)}
                    onCollapse={() => setCollapsed(true)}
                    testId={
                      sidebarExpanded && isCatalog
                        ? "discovery-sidebar"
                        : "discovery-sidebar-compact"
                    }
                  />
                </Box>
              </Fade>
            )}
          </Box>
        )}

        {/* Content pages float the expanded nav over the page so centered
            content never shifts. Slide it in from the left (matching the grid
            ease) rather than letting it appear instantly. */}
        {sidebar && !isCatalog && (
          <Slide
            direction="right"
            in={sidebarOverlaysContent}
            // When a catalog route switches to a content route, this Slide is
            // mounted already open. Skipping its appear transition prevents
            // MUI from measuring a child ref before the route commit has
            // attached it.
            appear={false}
            timeout={transitionMs}
            mountOnEnter
            unmountOnExit
          >
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                // Fixed to the viewport so opening it while scrolled down still
                // presents the nav, spanning the full height below the topbar.
                position: "fixed",
                zIndex: 10,
                top: `${stickyContentTop}px`,
                left: 0,
                width: `${SIDEBAR_WIDTH}px`,
                height: `calc(100vh - ${stickyContentTop}px)`,
                overflowY: "auto",
                overflowX: "hidden",
                // The expanded nav is light-styled, so it stays a light surface
                // even over dark pages.
                backgroundColor: "paper.primary",
                boxShadow: 3,
              }}
            >
              <DiscoverySidebar
                testId="discovery-sidebar-overlay"
                onNavigate={() => setCollapsed(true)}
                onCollapse={() => setCollapsed(true)}
              />
            </Box>
          </Slide>
        )}

        <Box
          component="main"
          sx={{
            minWidth: 0,
            ...(disableGutters ? {} : { px: CONTENT_GUTTER, py: { xs: 2, md: 3 } }),
            // Clear the fixed mobile tab bar so nothing hides behind it. Pages
            // with a footer get their clearance from the footer's own padding.
            ...(sidebar && bottomTabBar && footer === "none" && {
              pb: {
                xs: `calc(${BOTTOM_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom) + 8px)`,
                md: undefined,
              },
            }),
          }}
        >
          {children}
        </Box>

        {rightRail && (
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "sticky",
              top: `${TOPBAR_HEIGHT}px`,
              px: 2,
              py: 3,
            }}
          >
            {rightRail}
          </Box>
        )}
      </Box>

      {/* Full-width site footer at the true bottom of the page. Pad past the
          fixed mobile tab bar so its last line isn't hidden. */}
      {footer !== "none" && (
        <Box
          sx={
            sidebar && bottomTabBar
              ? { pb: { xs: `calc(${BOTTOM_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`, md: 0 } }
              : undefined
          }
        >
          {footer === "minimal" ? (
            <Footer />
          ) : (
            // Dark pages get the dark footer automatically; "expanded-dark" forces
            // it on otherwise-light pages (e.g. the creator pages).
            <ExpandedFooter variant={footer === "expanded-dark" || variant === "dark" ? "dark" : "light"} />
          )}
        </Box>
      )}

      {sidebar && bottomTabBar && (
        <BottomTabBar onBrowseClick={() => setBrowseOpen(true)} browseActive={browseOpen} />
      )}
      {sidebar && <BrowseSheet open={browseOpen} onClose={() => setBrowseOpen(false)} />}
    </Box>
  );
};

export default DiscoveryShell;
